import { Grid, IconButton, makeStyles, Typography, Drawer, Box, Divider, Fade, Hidden} from "@material-ui/core";
import { TestDriveBookingStepper } from "../TestDriveStepper";
import { useDispatch, useSelector } from "react-redux";
import { updateTestDriveBooking } from "../../../../../actions/testDriveBooking/actions";
import { TestDriveBookingSteps, ITestDriveVehicleGroup } from "../../../../../reducers/testDriveBooking/types";
import { IAppState } from "../../../../../store";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { useSnackBar } from "../../../../SnackBarContext/SnackBarContext";
import { GET_GROUPED_VEHICLES_FROM_GROUP } from "../../../../../graphql/bookings/getGroupedVehiclesFromGroup";
import { useEffect, useState, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import { ApolloError } from "apollo-boost";
import { SnackBarVariant } from "../../../../SnackbarWrapper/SnackbarWrapper";
import { formatGraphQLErrorMessage, getVehicleTypeIcon } from "../../../../utils";
import { Skeleton } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { capitalizeFirstLetter } from "../../utils";
import { getSignedUrl } from "../../../../../utils/getSignedUrl";
import TestDriveVehicleDetailDrawer from "./TestDriveVehicleDetailDrawer"

const useStyles = makeStyles((theme) => ({
  title: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },

  drawerPaper: {
    width: "100%",
    maxWidth: "100%",
    backgroundColor: "#fff",
    transition: "all 0.5s ease",
    overflow: "hidden",
  },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
  },
  drawerGroupInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  drawerGroupImage: {
    width: 54,
    height: 54,
    borderRadius: 6,
    overflow: "hidden",
    "& img": {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
  },

  splitLayout: {
    display: "flex",
    height: "100%",
    overflow: "hidden",
    flexDirection: "row",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },

  leftNav: {
    display: "flex",
    flexDirection: "column",
    width: "15%",
    borderRight: "1px solid #e5e7eb",
    overflowY: "auto",
    background: "#f9fafb",
    padding: theme.spacing(2),
    transition: "all 0.4s ease",
    "&::-webkit-scrollbar": { width: 6 },
    "&::-webkit-scrollbar-thumb": {
      borderRadius: 4,
      background: "#d1d5db",
    },

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      borderRight: "none",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      flexDirection: "row",
      overflowX: "auto",
      overflowY: "hidden",
      padding: theme.spacing(1.25, 1),
      gap: theme.spacing(1.25),
      "&::-webkit-scrollbar": { height: 0 },
      "&::-webkit-scrollbar-thumb": {
        borderRadius: 0,
        background: "transparent",
      },
      backdropFilter: "blur(6px)",
    },
  },

  leftVehicleCard: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 10,
    border: "1px solid transparent",
    background: "#fff",
    marginBottom: theme.spacing(2),
    transition: "all 0.25s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-3px)",
      borderColor: "#d1d5db",
      boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
    },

    [theme.breakpoints.down("sm")]: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: 0,
      padding: theme.spacing(1),
      border: "1px solid #eef2f8",
      borderRadius: 6,
    },
  },

  leftVehicleActive: {
    borderColor: "#2563eb !important",
    background: "#eff6ff",
    boxShadow: "0 0 12px rgba(37,99,235,0.18)",
    transform: "translateY(-3px)",
  },

  leftVehicleImage: {
    background: "#f3f4f6",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    height: 120,
    "& img": {
      width: "100%",
      height: "auto",
      objectFit: "contain",
      maxWidth: "170px",
    },

    [theme.breakpoints.down("sm")]: {
      flex: "0 0 56px",
      width: 56,
      height: 40,
      borderRadius: 8,
      "& img": { width: "100%", height: "100%", objectFit: "contain" },
    },
  },

  leftVehicleInfo: {
    padding: theme.spacing(1.2, 1.5),
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  scrollButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 20,
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(6px)",
    boxShadow: "0 6px 18px rgba(2,6,23,0.12)",
    display: "none",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    transition: "all 0.18s ease",

    [theme.breakpoints.down("md")]: {
      display: "flex",
    },

    "&:hover": {
      transform: "translateY(-50%) scale(1.06)",
    },
  },

  scrollButtonLeft: {
    left: 8,
  },
  scrollButtonRight: {
    right: 8,
  },

  rightDetailPane: {
    width: "85%",
    flexGrow: 1,
    overflowY: "auto",
    backgroundColor: "#fff",
    transition: "all 0.4s ease",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    }
  },
}));

export const VehicleSelection: React.FC = () => {
  const dispatch = useDispatch();
  const snackbar = useSnackBar();
  const classes = useStyles();
  const [vehicleGroups, setVehicleGroups] = useState<ITestDriveVehicleGroup[]>([]);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number>(0);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState<number>(0);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [processedImages, setProcessedImages] = useState<{[key: string]: string}>({});
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  
  const testDriveBookingState = useSelector(
    (state: IAppState) => state.testDriveBookingReducer
  );

  const appSetting = useSelector(
    (state: IAppState) => state.consumerWebsiteReducer.consumerWebsite
  );

  const selectedGroup = vehicleGroups[selectedGroupIndex];
  const selectedVehicleId = selectedGroup?.vehicleIds[selectedVehicleIndex];
  const totalVehicles = vehicleGroups.reduce((sum, group) => sum + group.vehicleIds.length, 0);

  const getImage = async (imageUrl: string): Promise<string> => {
    try {
      const signedUrl = await getSignedUrl(imageUrl);
      const response = await fetch(signedUrl as string);
      if (response.ok) {
        return signedUrl as string;
      }
    } catch (error) {
        snackbar({
          message:t('snackBar:errorFetchingSignedUrl'),
          variant: SnackBarVariant.ERROR,
        });
    }
    return "";
  };

  const processVehicleImages = async (vehicleGroups: ITestDriveVehicleGroup[]) => {
    const imagePromises: Promise<void>[] = [];
    const newProcessedImages: {[key: string]: string} = {};

    vehicleGroups.forEach((group) => {
      if (group.imageUrl) {
        const imageUrl = group.imageUrl;
        const promise = getImage(imageUrl).then((processedUrl) => {
          if (processedUrl) {
            newProcessedImages[imageUrl] = processedUrl;
          }
        });
        imagePromises.push(promise);
      }
    });

    await Promise.all(imagePromises);
    setProcessedImages(prev => ({ ...prev, ...newProcessedImages }));
  };

  const [getGroupedVehicles, { loading: vehiclesLoading }] = useLazyQuery(
    GET_GROUPED_VEHICLES_FROM_GROUP,
    {
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        if (data.getGroupedVehiclesFromGroup && data.getGroupedVehiclesFromGroup.length > 0) {
          setVehicleGroups(data.getGroupedVehiclesFromGroup);
          processVehicleImages(data.getGroupedVehiclesFromGroup);
          
          const firstGroup = data.getGroupedVehiclesFromGroup[0];
          setSelectedGroupIndex(0);
          setSelectedVehicleIndex(0);
          setDrawerOpen(true);
            
          dispatch(
            updateTestDriveBooking({
              ...testDriveBookingState,
              availableVehicleIds: data.getGroupedVehiclesFromGroup.flatMap((g: ITestDriveVehicleGroup) => g.vehicleIds),
              stepperData: {
                ...testDriveBookingState.stepperData,
                model: firstGroup.vehicleName,
              },
              vehicleImage: firstGroup.imageUrl || "",
            })
          );
        }
      },
      onError: (error: ApolloError) => {
        snackbar({
          message: formatGraphQLErrorMessage(error.message),
          variant: SnackBarVariant.ERROR,
        });
      },
    }
  );

  useEffect(() => {
    if (
      testDriveBookingState.branchId &&
      testDriveBookingState.vehicleGroupId &&
      appSetting.id
    ) {
      getGroupedVehicles({
        variables: {
          branchId: testDriveBookingState.branchId,
          vehicleGroupId: testDriveBookingState.vehicleGroupId,
          appSettingId: appSetting.id,
        },
      });
    }
  }, [testDriveBookingState.branchId, testDriveBookingState.vehicleGroupId, appSetting.id, getGroupedVehicles]);

  const prevStep = () => {
    dispatch(
      updateTestDriveBooking({
        ...testDriveBookingState,
        currentStep: TestDriveBookingSteps.BRANCH_SELECTION,
      })
    );
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleVehicleSelect = (groupIdx: number, vehicleIdx: number) => {
    setSelectedGroupIndex(groupIdx);
    setSelectedVehicleIndex(vehicleIdx);
  };

  if (vehiclesLoading) {
    return (
      <Grid item container spacing={2}>
        <Grid
          item
          container
          xs={12}
          spacing={1}
          alignItems="flex-start"
          justify="flex-start"
        >
          <Grid item>
            <IconButton aria-label="previous-step" onClick={prevStep}>
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item xs={10}>
            <Typography variant="h1" component={"h1"}>
              {t("root:selectCar")}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <TestDriveBookingStepper />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <Skeleton variant="rect" width="100%" height={400} />
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      {drawerOpen && vehicleGroups.length > 0 && (
        <Drawer
          anchor="right"
          open={drawerOpen}
          onClose={handleDrawerClose}
          transitionDuration={400}
          classes={{ paper: classes.drawerPaper }}
        >
          <Box className={classes.drawerHeader}>
            <Box className={classes.drawerGroupInfo}>
              <IconButton aria-label="previous-step" onClick={prevStep}>
                <ArrowBackIcon />
              </IconButton>
              <Box className={classes.drawerGroupImage}>
                {selectedGroup?.imageUrl && processedImages[selectedGroup.imageUrl] ? (
                  <img src={processedImages[selectedGroup.imageUrl]} alt={selectedGroup.vehicleName} />
                ) : (
                  <img
                    src={getVehicleTypeIcon(capitalizeFirstLetter(selectedGroup?.bodyType || "COUPE"))}
                    alt={selectedGroup?.vehicleName || "Vehicle"}
                  />
                )}
              </Box>
              <Box>
                <Typography style={{ fontWeight: 700, fontSize: 20, color: "#111827" }}>
                  {selectedGroup?.vehicleName || "Select Vehicle"}
                </Typography>
                <Typography style={{ color: "#6b7280", fontSize: 13 }}>
                  {t("root:available")}: {totalVehicles}
                </Typography>
              </Box>
            </Box>
            <Box />
          </Box>

          <Divider />

          <Fade in={drawerOpen} timeout={400} unmountOnExit>
            <Box className={classes.splitLayout}>
              <Hidden smDown>
                <Box className={classes.leftNav}>
                  {vehicleGroups.map((group, groupIdx) => 
                    group.vehicleIds.map((vehicleId, vehicleIdx) => {
                      const isActive = groupIdx === selectedGroupIndex && vehicleIdx === selectedVehicleIndex;
                      const displayName = group.vehicleIds.length > 1 
                        ? `${group.vehicleName} #${vehicleIdx + 1}` 
                        : group.vehicleName;
                      
                      return (
                        <Box
                          key={`${groupIdx}-${vehicleIdx}`}
                          className={`${classes.leftVehicleCard} ${
                            isActive ? classes.leftVehicleActive : ""
                          }`}
                          onClick={() => handleVehicleSelect(groupIdx, vehicleIdx)}
                        >
                          <Box className={classes.leftVehicleImage}>
                            <img
                              src={
                                (group.imageUrl && processedImages[group.imageUrl]) ||
                                getVehicleTypeIcon(capitalizeFirstLetter(group.bodyType || ""))
                              }
                              alt={displayName}
                            />
                          </Box>
                          <Box className={classes.leftVehicleInfo}>
                            <Typography style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
                              {displayName}
                            </Typography>
                            <Typography style={{ fontSize: 12, color: "#6b7280" }}>
                              {group.seats} Seats • {group.doors} Doors
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Hidden>

              <Hidden smUp>
                <Box position="relative" style={{ padding: 0 }}>
                  <Box
                    className={`${classes.scrollButton} ${classes.scrollButtonLeft}`}
                    onClick={() => {
                      scrollerRef.current?.scrollBy({ left: -260, behavior: "smooth" });
                    }}
                    aria-hidden
                  >
                    <ChevronLeftIcon style={{ fontSize: 18 }} />
                  </Box>

                  <div ref={scrollerRef as React.Ref<HTMLDivElement>} className={classes.leftNav}>
                    {vehicleGroups.map((group, groupIdx) => 
                      group.vehicleIds.map((vehicleId, vehicleIdx) => {
                        const isActive = groupIdx === selectedGroupIndex && vehicleIdx === selectedVehicleIndex;
                        
                        return (
                          <Box
                            key={`${groupIdx}-${vehicleIdx}`}
                            className={`${classes.leftVehicleCard} ${
                              isActive ? classes.leftVehicleActive : ""
                            }`}
                            onClick={() => handleVehicleSelect(groupIdx, vehicleIdx)}
                          >
                            <Box className={classes.leftVehicleImage}>
                              <img
                                src={
                                  (group.imageUrl && processedImages[group.imageUrl]) ||
                                  getVehicleTypeIcon(capitalizeFirstLetter(group.bodyType || ""))
                                }
                                alt={group.vehicleName}
                              />
                            </Box>
                          </Box>
                        );
                      })
                    )}
                  </div>

                  <Box
                    className={`${classes.scrollButton} ${classes.scrollButtonRight}`}
                    onClick={() => {
                      scrollerRef.current?.scrollBy({ left: 260, behavior: "smooth" });
                    }}
                    aria-hidden
                  >
                    <ChevronRightIcon style={{ fontSize: 18 }} />
                  </Box>
                </Box>
              </Hidden>

              <Box className={classes.rightDetailPane}>
                {selectedGroup && (
                  <TestDriveVehicleDetailDrawer
                    vehicleGroup={selectedGroup}
                    selectedVehicleId={selectedVehicleId}
                    selectedVehicleIndex={selectedVehicleIndex}
                    processedImages={processedImages}
                  />
                )}
              </Box>
            </Box>
          </Fade>
        </Drawer>
      )}
    </>
  );
};
